import axios from "axios";
import momentTz from "moment-timezone";
import { toast } from "react-toastify";
import { CarrierEditOptionsDto } from "models/dto/CarrierEditOptionsDto";
import { CarrierDto } from "models/dto/CarrierDto";
import { CarrierAttributeDto } from "models/dto/CarrierAttributeDto";
import { GetProfilesDocumentPageQuery } from "models/dto/GetProfilesDocumentPageQuery";
import { ConnectedCompanyDto } from "models/dto/ConnectedCompanyDto";
import { CarrierHistoryFilterOptionsDto } from "models/dto/CarrierHistoryFilterOptionsDto";
import { CarrierHistoryFilter } from "models/CarrierHistoryFilter";
import { displayTimeZone } from "shared/utils/dateTimeFormatting";

type CarrierHistoryGridState = {
  dir: string;
  page: number;
  pageSize: number;
  sortField: string;
};

export const fetchCarrierById = (carrierId: string) => {
  return axios.get<CarrierDto>(`/api/carriers/${carrierId}`);
};

export const starCarrier = (carrierId: string) => {
  return axios.post(`/api/carriers/${carrierId}/star`, { carrierId });
};

export const fetchCarrierSummary = (carrierId: string) => {
  return axios.get(`/api/carriers/${carrierId}/summary`);
};

export const fetchCarrierOwners = (searchQuery: string) => {
  return axios.get(
    `/api/users/turvo/list?name=${searchQuery}&page=1&pageSize=100`
  );
};

export const searchConnectedCompanies = (
  carrierId: number,
  searchQuery: string
) => {
  return axios.get<ConnectedCompanyDto[]>(
    `/api/carriers/${carrierId}/connectedCompanies/search`,
    { params: { name: searchQuery } }
  );
};

export const fetchCarrierOptions = () => {
  return axios.get<CarrierEditOptionsDto>("/api/carriers/options/edit");
};

export const saveCarrier = async (carrier: CarrierDto) => {
  try {
    await axios.put("/api/carriers", carrier);
    toast.success("Carrier details have been saved!");
  } catch {
    return toast.error("Error occured while saving carrier details!");
  }
};

export const fetchCarrierAttributes = () => {
  return axios.get(`/api/carriers/attributes`);
};

export const saveCarrierAttributes = async ({
  carrierId,
  attributeList,
}: {
  carrierId: string;
  attributeList: CarrierAttributeDto[];
}) => {
  try {
    await axios.put(`/api/carriers/${carrierId}/attributes`, {
      attributes: attributeList,
    });
    toast.success("Carrier attributes have been saved!");
  } catch (err) {
    return toast.error("Error occured while saving carrier attributes!");
  }
};

const startOfDisplayDay = (date: string) =>
  momentTz.tz(date, displayTimeZone).toISOString();

const startOfNextDisplayDay = (date: string) =>
  momentTz.tz(date, displayTimeZone).add(1, "day").toISOString();

const appendCarrierHistoryFilterParams = (
  params: URLSearchParams,
  filter: CarrierHistoryFilter
) => {
  filter.origins.forEach((origin) => params.append("origins", origin));

  filter.destinations.forEach((destination) =>
    params.append("destinations", destination)
  );

  filter.itemsCounts.forEach((itemsCount) =>
    params.append("itemsCounts", itemsCount.toString())
  );

  filter.miles.forEach((miles) => params.append("miles", miles.toString()));

  filter.rates.forEach((rate) => params.append("rates", rate.toString()));

  filter.loadStatuses.forEach((loadStatus) =>
    params.append("loadStatuses", loadStatus)
  );

  filter.customIds.forEach((customId) => params.append("customIds", customId));

  if (filter.pickupFrom) {
    params.append("pickupFrom", startOfDisplayDay(filter.pickupFrom));
  }

  if (filter.pickupTo) {
    params.append("pickupBefore", startOfNextDisplayDay(filter.pickupTo));
  }

  if (filter.coveredFrom) {
    params.append("coveredFrom", startOfDisplayDay(filter.coveredFrom));
  }

  if (filter.coveredTo) {
    params.append("coveredBefore", startOfNextDisplayDay(filter.coveredTo));
  }
};

export const fetchCarrierHistory = ({
  carrierId,
  gridState,
  filter,
}: {
  carrierId: string;
  gridState: CarrierHistoryGridState;
  filter: CarrierHistoryFilter;
}) => {
  const params = new URLSearchParams({
    carrierId,
    page: gridState.page.toString(),
    pageSize: gridState.pageSize.toString(),
    sortField: gridState.sortField,
    dir: gridState.dir,
  });

  appendCarrierHistoryFilterParams(params, filter);

  return axios.get(`/api/carrierHistory/list?${params}`);
};

export const fetchCarrierHistoryFilterOptions = (carrierId: string) => {
  return axios.get<CarrierHistoryFilterOptionsDto>(
    "/api/carrierHistory/filterOptions",
    { params: { carrierId } }
  );
};

export const fetchCarrierDocuments = (
  carrierId: number,
  gridState: { pageSize: number; page: number }
) => {
  return axios.get(
    `/api/carriers/${carrierId}/document/list?carrierId=${carrierId}&pageSize=${gridState.pageSize}&pageNumber=${gridState.page}`
  );
};

export const filterCarrierDocuments = (
  carrierId: number,
  gridState: GetProfilesDocumentPageQuery
) => {
  return axios.get(
    `/api/carriers/${carrierId}/document/search?carrierId=${carrierId}`,
    { params: gridState }
  );
};

export const saveDocumentNotes = async (
  carrierId: number,
  notes: {
    id: number;
    notes: string;
  }
) => {
  try {
    await axios.put(`/api/carriers/${carrierId}/document/notes`, notes);
    toast.success("Document notes have been saved!");
  } catch (err) {
    return toast.error("Error occured while saving document notes!");
  }
};

export const updateCarrierDocument = (
  carrierId: number,
  formData: FormData
) => {
  return axios.put(`/api/carriers/${carrierId}/document`, formData);
};

export const addCarrierDocument = (carrierId: number, formData: FormData) => {
  return axios.post(`/api/carriers/${carrierId}/document`, formData);
};

export const deleteCarrierDocument = async (
  carrierId: number,
  documentId: number
) => {
  try {
    await axios.delete(`/api/carriers/${carrierId}/document/${documentId}`);
    toast.success("Document has been deleted!");
  } catch (err) {
    return toast.error("Error occured while deleting document!");
  }
};
